/**
 * Single shared Server-Sent Events connection (ADR-0029). The whole SPA holds
 * exactly ONE EventSource to `/api/notifications/stream`, multiplexing every
 * event type (`inbox` nudges for signed-in viewers, `public` catalog-activity
 * for everyone). Consumers subscribe by event name; the connection opens on the
 * first subscriber and closes on the last, so an idle app holds nothing.
 *
 * Connection economy (the home-server socket budget the ADR is calibrated to):
 *  - ONE connection per tab, never one-per-notification.
 *  - The stream is dropped while the tab is backgrounded (visibilitychange) and
 *    reopened when it returns to the foreground.
 *  - On error (a transient drop OR a 503 capacity rejection, indistinguishable
 *    from the client) we close and retry on a fixed backoff rather than letting
 *    EventSource hot-reconnect against a capped server. Consumers fall back to
 *    polling while disconnected (see `useInbox`).
 */

type EventName = 'inbox' | 'public';
type DataHandler = (data: string) => void;
type StateListener = (connected: boolean) => void;

const STREAM_URL = '/api/notifications/stream';
const RECONNECT_MS = 15_000;

const handlers: Record<EventName, Set<DataHandler>> = { inbox: new Set(), public: new Set() };
const stateListeners = new Set<StateListener>();

let source: EventSource | null = null;
let refCount = 0;
let reconnectTimer: number | null = null;
let connected = false;

function setConnected(next: boolean): void {
    if (connected === next) return;
    connected = next;
    stateListeners.forEach((l) => l(next));
}

function open(): void {
    if (source || refCount === 0) return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const es = new EventSource(STREAM_URL, { withCredentials: true });
    source = es;

    es.addEventListener('open', () => setConnected(true));

    (Object.keys(handlers) as EventName[]).forEach((name) => {
        es.addEventListener(name, (event) => {
            const data = (event as MessageEvent).data as string;
            handlers[name].forEach((h) => h(data));
        });
    });

    es.addEventListener('error', () => {
        // Covers a transient network drop AND a 503 cap rejection (the client
        // can't tell them apart). Close, signal consumers to poll, retry later.
        close();
        setConnected(false);
        scheduleReconnect();
    });
}

function close(): void {
    if (source) {
        source.close();
        source = null;
    }
}

function scheduleReconnect(): void {
    if (reconnectTimer !== null || refCount === 0) return;
    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        open();
    }, RECONNECT_MS);
}

function clearReconnect(): void {
    if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
}

function onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
        open();
    } else {
        clearReconnect();
        close();
        setConnected(false);
    }
}

/**
 * Subscribe to one stream event. Optionally also observe connection state (used
 * by the inbox to toggle its polling fallback). Returns an unsubscribe function;
 * the shared connection is reference-counted and torn down on the last release.
 */
export function subscribeStream(
    name: EventName,
    handler: DataHandler,
    onConnectedChange?: StateListener,
): () => void {
    handlers[name].add(handler);
    if (onConnectedChange) stateListeners.add(onConnectedChange);

    if (refCount === 0 && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
    refCount += 1;
    open();
    // Seed the new state listener with the current connection state.
    if (onConnectedChange) onConnectedChange(connected);

    return () => {
        handlers[name].delete(handler);
        if (onConnectedChange) stateListeners.delete(onConnectedChange);
        refCount -= 1;
        if (refCount === 0) {
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
            clearReconnect();
            close();
            connected = false;
        }
    };
}
